import { WabunkaExperienceListResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience';

import { useAsyncFetchWithUniqueKeyFromPublicWabunkaApi } from '@/composables/models/wabunka/useAsyncFetchWithUniqueKeyFromPublicWabunkaApi';
import { useAsyncDataRequestStatus } from '@/composables/utils/useAsyncDataRequestStatus';
import { buildWabunkaExperienceListViewModel } from '@/models/wabunka/wabunkaExperience';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';

export interface FetchExperiencesQuery {
  experienceEventStartAtDates?: string;
  destinationTagWebPaths?: string;
  themeTagWebPaths?: string;
  page?: string;
  slugs?: string;
  perPage: string;
  includes: string;
  currency?: string;
}

/**
 * 体験一覧情報の検索に使用する Composable
 * Page でのみ使用可能
 */
export const useFetchExperiencesQuery = async (query: {
  experienceEventStartAtDates: Ref<string>;
  destinations: Ref<string>;
  themes: Ref<string>;
  slugs: Ref<string[]>;
  perPage?: Ref<number>;
  page?: Ref<number>;
}) => {
  const path = computed(() => `/experiences/search`);
  const { selectedCurrency } = useCurrency();

  const queryRef = computed<FetchExperiencesQuery>(() => {
    const queryObj: FetchExperiencesQuery = {
      page: query.page?.value ? String(query.page.value) : undefined,
      perPage: query.perPage?.value ? String(query.perPage.value) : '0', // 0 で全件取得
      includes: 'experienceImages',
      currency: selectedCurrency.value !== 'JPY' ? selectedCurrency.value : undefined,
    };

    if (query.experienceEventStartAtDates?.value) {
      queryObj.experienceEventStartAtDates = query.experienceEventStartAtDates?.value;
    }

    if (query.destinations?.value) {
      queryObj.destinationTagWebPaths = query.destinations?.value;
    }

    if (query.themes?.value) {
      queryObj.themeTagWebPaths = query.themes?.value;
    }

    if (query.slugs?.value?.length > 0) {
      queryObj.slugs = query.slugs?.value.join(',');
    }

    return queryObj;
  });

  const { data, refresh, status } = await useAsyncFetchWithUniqueKeyFromPublicWabunkaApi({
    path,
    query: queryRef,
    options: { server: false, immediate: true },
    converter: WabunkaExperienceListResponse.fromJSON,
  });

  const experiences = computed(() => buildWabunkaExperienceListViewModel(data.value?.items));
  const totalCount = computed<number>(() => data.value?.maxCount ?? 0);
  const meta = computed(() => data.value?.searchResultPageMeta);

  return { experiences, meta, totalCount, refresh, ...useAsyncDataRequestStatus(status) };
};
